// client/src/utils/licenseUtils.js
import { LicenseType, LicenseTypeLabels } from "../constants/licenseTypes";

/**
 * Extracts license information from license data object or legacy hex string
 * @param {string|object} licenseData - The license data field
 * @returns {object} - Object containing licenseType and minimumTimestamp
 */
export const extractLicenseInfo = (licenseData) => {
  try {
    // If it's already an object (new format)
    if (typeof licenseData === "object" && licenseData !== null) {
      return {
        licenseType: licenseData.licenseType || LicenseType.UNKNOWN,
        minimumTimestamp: licenseData.minimumTimestamp || null,
        isNewFormat: true,
      };
    }

    // If it's a string, try to parse as JSON first (new format as string)
    if (typeof licenseData === "string") {
      try {
        const parsed = JSON.parse(licenseData);
        return {
          licenseType: parsed.licenseType || LicenseType.UNKNOWN,
          minimumTimestamp: parsed.minimumTimestamp || null,
          isNewFormat: true,
        };
      } catch (jsonError) {
        // If JSON parsing fails, try legacy hex format
        if (licenseData.startsWith("0")) {
          try {
            const hexData = licenseData.substring(1);
            const jsonString = Buffer.from(hexData, "hex").toString();
            const parsed = JSON.parse(jsonString);
            return {
              licenseType: parsed.licenseType || LicenseType.UNKNOWN,
              minimumTimestamp: parsed.minimumTimestamp || null,
              isNewFormat: true,
            };
          } catch (hexError) {
            // Legacy format - just a timestamp
            const timestamp = parseInt(licenseData.substring(1), 16);
            return {
              licenseType: LicenseType.UNKNOWN,
              minimumTimestamp: timestamp || null,
              isNewFormat: false,
            };
          }
        } else {
          // Unknown format
          return {
            licenseType: LicenseType.UNKNOWN,
            minimumTimestamp: null,
            isNewFormat: false,
          };
        }
      }
    }

    // Unknown format
    return {
      licenseType: LicenseType.UNKNOWN,
      minimumTimestamp: null,
      isNewFormat: false,
    };
  } catch (error) {
    console.error("Error extracting license info:", error);
    return {
      licenseType: LicenseType.UNKNOWN,
      minimumTimestamp: null,
      isNewFormat: false,
    };
  }
};

/**
 * Validates if a license type is valid
 * @param {number} licenseType - The license type to validate
 * @returns {boolean} - True if valid
 */
export const isValidLicenseType = (licenseType) => {
  return Object.values(LicenseType).includes(licenseType);
};

/**
 * Gets a user-friendly display name for a license type
 * @param {number} licenseType - The license type
 * @returns {string} - Display name
 */
export const getLicenseTypeDisplayName = (licenseType) => {
  return LicenseTypeLabels[licenseType] || "Unknown";
};
